import React, { useEffect, useState } from 'react';
import { UserCheck } from 'lucide-react';
import { studentExamApi } from '../../api/examApi';

/**
 * Attendance gate — UX mirror of the server-side rule.
 *
 * A student may enter an exam session or upload a submission only if they are
 * marked PRESENT for today. Absent or unmarked -> blocked. The SERVER is the
 * enforcement point (start_attempt / question-pdf / upload all 403 on their
 * own); this hook exists purely so the student sees a clear reason instead of
 * a button that dead-ends in a 403.
 *
 * Fail-open on a network/API error: `allowed` stays true so a transient blip
 * never hides a legitimately available exam. The server still blocks anyone
 * who isn't actually present, so failing open here costs nothing.
 */
export function useExamAccess() {
  const [state, setState] = useState({ loading: true, allowed: true, status: null, reason: null });

  useEffect(() => {
    let cancelled = false;
    studentExamApi
      .getAttendanceToday()
      .then(({ data }) => {
        if (cancelled) return;
        setState({
          loading: false,
          allowed: data.exam_access_allowed,
          status: data.attendance_status,
          reason: data.reason,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ loading: false, allowed: true, status: null, reason: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function attendanceBlockedMessage(status) {
  if (status === 'absent') {
    return "You're marked absent today, so exams are not available. Speak to your faculty if this is wrong.";
  }
  return 'Your attendance for today has not been marked yet. Exams open once your faculty takes the roll.';
}

/** Banner shown above an exam list when the gate is blocking entry. */
export function ExamAccessBanner({ status }) {
  return (
    <div
      className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4"
      data-testid="exam-access-banner"
    >
      <UserCheck size={20} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div>
        <p className="font-medium text-amber-900 dark:text-amber-200">Exams unavailable today</p>
        <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-300">
          {attendanceBlockedMessage(status)}
        </p>
      </div>
    </div>
  );
}

/** Inline replacement for the Start/Continue button when the gate is blocking. */
export function ExamAccessBlockedButton() {
  return (
    <span
      className="whitespace-nowrap rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-300"
      data-testid="exam-blocked-btn"
      title="Attendance not marked present for today"
    >
      Not available
    </span>
  );
}
